import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Spinner, Alert, Modal, InputGroup, Collapse } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { getSubmissions, updateSubmission } from '../api/submissionsApi';
import { FaEdit, FaSave, FaTimes, FaSearch, FaFilter, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import '../styles/FieldMappings.css';

// Status badge colors
const statusColors = {
  submitted: 'primary',
  approved: 'success',
  declined: 'danger',
  pending: 'warning'
};

// Status display names
const statusLabels = {
  submitted: 'Submitted',
  approved: 'Approved',
  declined: 'Declined',
  pending: 'Pending'
};

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format short date (just date, no time)
const formatShortDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const Submissions = () => {
  const { user } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  
  const isAdmin = user?.role === 'super_admin' || user?.role === 'developer';
  
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  
  // Expanded rows state (track which deal names are expanded)
  const [expandedDeals, setExpandedDeals] = useState({});
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [lenderFilter, setLenderFilter] = useState('all');
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [editFormData, setEditFormData] = useState({
    status: '',
    approvalAmount: '',
    fundedAmount: '',
    termLength: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Load submissions
  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getSubmissions();
      
      if (result.success) {
        setSubmissions(result.submissions);
        setFilteredSubmissions(result.submissions);
      } else {
        throw new Error(result.message || 'Failed to load submissions');
      }
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError(err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    loadSubmissions();
    setTimeout(() => setPageLoaded(true), 100);
  }, [loadSubmissions]);
  
  // Apply filters
  useEffect(() => {
    let filtered = [...submissions];
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(sub => 
        sub.dealName?.toLowerCase().includes(search) ||
        sub.lenderName?.toLowerCase().includes(search) ||
        sub.submittedBy?.toLowerCase().includes(search)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }
    
    // Lender filter
    if (lenderFilter !== 'all') {
      filtered = filtered.filter(sub => sub.lenderId === lenderFilter);
    }
    
    setFilteredSubmissions(filtered);
  }, [submissions, searchTerm, statusFilter, lenderFilter]);
  
  // Group submissions by deal name
  const groupedSubmissions = useMemo(() => {
    const groups = {};
    
    filteredSubmissions.forEach(sub => {
      const dealName = sub.dealName || 'Unknown Business';
      if (!groups[dealName]) {
        groups[dealName] = [];
      }
      groups[dealName].push(sub);
    });
    
    // Sort each group by submission date (newest first)
    Object.keys(groups).forEach(dealName => {
      groups[dealName].sort((a, b) => new Date(b.submissionDate) - new Date(a.submissionDate));
    });
    
    // Convert to array and sort by most recent submission date
    return Object.entries(groups)
      .map(([dealName, subs]) => ({
        dealName,
        submissions: subs,
        latestSubmission: subs[0],
        submissionCount: subs.length
      }))
      .sort((a, b) => new Date(b.latestSubmission.submissionDate) - new Date(a.latestSubmission.submissionDate));
  }, [filteredSubmissions]);
  
  // Get unique lenders for filter
  const uniqueLenders = [...new Set(submissions.map(sub => sub.lenderId))].filter(Boolean);
  
  // Toggle expanded state for a deal
  const toggleExpanded = (dealName) => {
    setExpandedDeals(prev => ({
      ...prev,
      [dealName]: !prev[dealName]
    }));
  };
  
  // Open edit modal
  const handleEditClick = (submission, e) => {
    if (e) e.stopPropagation();
    setEditingSubmission(submission);
    setEditFormData({
      status: submission.status || 'submitted',
      approvalAmount: submission.approvalAmount || '',
      fundedAmount: submission.fundedAmount || '',
      termLength: submission.termLength || ''
    });
    setEditModalOpen(true);
  };
  
  // Close edit modal
  const handleCloseModal = () => {
    setEditModalOpen(false);
    setEditingSubmission(null);
    setEditFormData({
      status: '',
      approvalAmount: '',
      fundedAmount: '',
      termLength: ''
    });
  };
  
  // Save edit
  const handleSaveEdit = async () => {
    if (!editingSubmission) return;
    
    try {
      setSaving(true);
      
      const updateData = {
        status: editFormData.status,
        approvalAmount: editFormData.approvalAmount ? Number(editFormData.approvalAmount) : null,
        fundedAmount: editFormData.fundedAmount ? Number(editFormData.fundedAmount) : null,
        termLength: editFormData.termLength || null
      };
      
      const result = await updateSubmission(editingSubmission._id, updateData);
      
      if (result.success) {
        notifySuccess('Submission updated successfully');
        handleCloseModal();
        loadSubmissions(); // Refresh the list
      } else {
        throw new Error(result.message || 'Failed to update submission');
      }
    } catch (err) {
      console.error('Error updating submission:', err);
      notifyError(err.message || 'Failed to update submission');
    } finally {
      setSaving(false);
    }
  };
  
  // Calculate stats
  const stats = {
    total: submissions.length,
    submitted: submissions.filter(s => s.status === 'submitted').length,
    approved: submissions.filter(s => s.status === 'approved').length,
    declined: submissions.filter(s => s.status === 'declined').length,
    pending: submissions.filter(s => s.status === 'pending').length,
    totalApproved: submissions
      .filter(s => s.status === 'approved')
      .reduce((sum, s) => sum + (s.approvalAmount || 0), 0),
    totalFunded: submissions
      .filter(s => s.fundedAmount)
      .reduce((sum, s) => sum + (s.fundedAmount || 0), 0)
  };
  
  // Get summary status for a deal (show best status)
  const getDealSummaryStatus = (subs) => {
    if (subs.some(s => s.status === 'approved')) return 'approved';
    if (subs.some(s => s.status === 'pending')) return 'pending';
    if (subs.some(s => s.status === 'declined')) return 'declined';
    return 'submitted';
  };
  
  return (
    <div className={`fade-in ${pageLoaded ? 'active' : ''}`}>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col className="text-center">
            <h2 className="mb-1">Submissions</h2>
            <p className="text-muted">
              {isAdmin ? 'View and manage all deal submissions' : 'View your deal submissions'}
            </p>
          </Col>
        </Row>
        
        {/* Stats Cards */}
        <Row className="mb-4">
          <Col md={3} sm={6} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <h3 className="mb-0 text-primary">{stats.total}</h3>
                <small className="text-muted">Total Submissions</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <h3 className="mb-0 text-success">{stats.approved}</h3>
                <small className="text-muted">Approved</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <h3 className="mb-0 text-success">{formatCurrency(stats.totalApproved)}</h3>
                <small className="text-muted">Total Approved</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <h3 className="mb-0 text-info">{formatCurrency(stats.totalFunded)}</h3>
                <small className="text-muted">Total Funded</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="align-items-end">
              <Col md={4} className="mb-3 mb-md-0">
                <Form.Label className="small text-muted mb-1">
                  <FaSearch className="me-1" /> Search
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by deal name, lender, or user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col md={3} className="mb-3 mb-md-0">
                <Form.Label className="small text-muted mb-1">
                  <FaFilter className="me-1" /> Status
                </Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="declined">Declined</option>
                  <option value="pending">Pending</option>
                </Form.Select>
              </Col>
              <Col md={3} className="mb-3 mb-md-0">
                <Form.Label className="small text-muted mb-1">
                  <FaFilter className="me-1" /> Lender
                </Form.Label>
                <Form.Select
                  value={lenderFilter}
                  onChange={(e) => setLenderFilter(e.target.value)}
                >
                  <option value="all">All Lenders</option>
                  {uniqueLenders.map(lender => (
                    <option key={lender} value={lender}>
                      {submissions.find(s => s.lenderId === lender)?.lenderName || lender}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button 
                  variant="outline-secondary" 
                  className="w-100"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setLenderFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        
        {/* Submissions Table */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading submissions...</p>
              </div>
            ) : groupedSubmissions.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted mb-0">
                  {submissions.length === 0 
                    ? 'No submissions found' 
                    : 'No submissions match your filters'}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Deal Name</th>
                      <th>Lenders</th>
                      <th>Last Submitted</th>
                      <th>Status</th>
                      {isAdmin && <th>Submitted By</th>}
                      <th style={{ width: '60px' }} className="text-center">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedSubmissions.map((group) => (
                      <React.Fragment key={group.dealName}>
                        {/* Main row for the deal */}
                        <tr 
                          onClick={() => toggleExpanded(group.dealName)}
                          style={{ cursor: 'pointer' }}
                          className={expandedDeals[group.dealName] ? 'table-active' : ''}
                        >
                          <td className="fw-medium align-middle">{group.dealName}</td>
                          <td className="align-middle">
                            {group.submissionCount === 1 ? (
                              <span>{group.latestSubmission.lenderName || group.latestSubmission.lenderId}</span>
                            ) : (
                              <Badge bg="secondary" pill>
                                {group.submissionCount} lenders
                              </Badge>
                            )}
                          </td>
                          <td className="text-muted small align-middle">
                            {formatShortDate(group.latestSubmission.submissionDate)}
                          </td>
                          <td className="align-middle">
                            {group.submissionCount === 1 ? (
                              <Badge bg={statusColors[group.latestSubmission.status] || 'secondary'}>
                                {statusLabels[group.latestSubmission.status] || group.latestSubmission.status || 'Unknown'}
                              </Badge>
                            ) : (
                              <Badge bg={statusColors[getDealSummaryStatus(group.submissions)] || 'secondary'}>
                                {statusLabels[getDealSummaryStatus(group.submissions)]}
                              </Badge>
                            )}
                          </td>
                          {isAdmin && (
                            <td className="text-muted small align-middle">
                              {group.latestSubmission.submittedBy || '-'}
                            </td>
                          )}
                          <td className="text-center align-middle">
                            {expandedDeals[group.dealName] ? (
                              <FaChevronDown style={{ fontSize: '1rem', color: '#6c757d' }} />
                            ) : (
                              <FaChevronRight style={{ fontSize: '1rem', color: '#6c757d' }} />
                            )}
                          </td>
                        </tr>
                        
                        {/* Expanded lender rows - always available for all deals */}
                        <tr>
                          <td colSpan={isAdmin ? 6 : 5} className="p-0 border-0">
                            <Collapse in={expandedDeals[group.dealName]}>
                              <div>
                                <Table size="sm" className="mb-0 bg-light">
                                  <thead>
                                    <tr className="small">
                                      <th style={{ paddingLeft: '2rem' }}>Lender</th>
                                      <th>Submitted</th>
                                      <th>Status</th>
                                      <th>Approval</th>
                                      <th>Funded</th>
                                      <th>Term</th>
                                      {isAdmin && <th className="text-center">Actions</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {group.submissions.map((sub) => (
                                      <tr key={sub._id}>
                                        <td style={{ paddingLeft: '2rem' }}>{sub.lenderName || sub.lenderId || '-'}</td>
                                        <td className="text-muted small">{formatDate(sub.submissionDate)}</td>
                                        <td>
                                          <Badge bg={statusColors[sub.status] || 'secondary'} className="small">
                                            {statusLabels[sub.status] || sub.status || 'Unknown'}
                                          </Badge>
                                        </td>
                                        <td>{formatCurrency(sub.approvalAmount)}</td>
                                        <td>{formatCurrency(sub.fundedAmount)}</td>
                                        <td>{sub.termLength || '-'}</td>
                                        {isAdmin && (
                                          <td className="text-center">
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              onClick={(e) => handleEditClick(sub, e)}
                                            >
                                              <FaEdit />
                                            </Button>
                                          </td>
                                        )}
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
          {filteredSubmissions.length > 0 && (
            <Card.Footer className="bg-light text-muted small">
              Showing {groupedSubmissions.length} deals ({filteredSubmissions.length} total submissions)
            </Card.Footer>
          )}
        </Card>
        
        {/* Edit Modal */}
        <Modal show={editModalOpen} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Submission</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editingSubmission && (
              <>
                <div className="mb-3 pb-3 border-bottom">
                  <strong>{editingSubmission.dealName}</strong>
                  <br />
                  <small className="text-muted">
                    {editingSubmission.lenderName} • {formatDate(editingSubmission.submissionDate)}
                  </small>
                </div>
                
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="submitted">Submitted</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                  </Form.Select>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Approval Amount</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      placeholder="0"
                      value={editFormData.approvalAmount}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, approvalAmount: e.target.value }))}
                    />
                  </InputGroup>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Funded Amount</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      placeholder="0"
                      value={editFormData.fundedAmount}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, fundedAmount: e.target.value }))}
                    />
                  </InputGroup>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Term Length</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., 12 months, 24 months"
                    value={editFormData.termLength}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, termLength: e.target.value }))}
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
              <FaTimes className="me-1" /> Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-1" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave className="me-1" /> Save Changes
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default Submissions;
